import type { Question, CompetencyCode, KnowledgeCode, MisconceptionCode } from '../types'
import { type AnnuitySeed, type BayesSeed, type OptimizationSeed, type OxyzSectionSeed, type OxyzSkewLinesSeed, type OxyzSphereSeed, type PharmacokineticsSeed, generateAnnuitySeed, generateBayesSeed, generateOptimizationSeed, generateOxyzSectionSeed, generateOxyzSkewLinesSeed, generateOxyzSphereSeed, generatePharmacokineticsSeed } from './generators/math_generators'
import { chapter1FunctionQuestions, generateChapter1Seed } from './ch1_functions_variants'
import { generateTrigonometrySeed, k11TrigonometryQuestions } from './k11_trigonometry_variants'
import { generateExpLogSeed, k11ExpLogQuestions } from './k11_exp_log_variants'
import { generateSequenceSeed, k11SequenceQuestions } from './k11_sequences_variants'
import { generateParallelSeed, k11ParallelGeometryQuestions } from './k11_parallel_geometry_variants'
import { generateLimitSeed, k11LimitContinuityQuestions } from './k11_limits_continuity_variants'
import { generateStatisticsSeed, k11GroupedStatisticsQuestions } from './k11_grouped_statistics_variants'
import { generateDerivativeSeed, k11DerivativeQuestions } from './k11_derivative_variants'

const mathMeta: Pick<Question, 'exam' | 'domain' | 'prerequisites' | 'misconceptionOptions'> = {
  exam: 'VACT',
  domain: 'Toán học',
  prerequisites: ['arithmetic'],
  misconceptionOptions: ['calculation-slip', 'concept-gap', 'careless-reading'],
}

const option = (id: string, text: string) => ({ id, text })

/** Keeps rounding from silently producing duplicate multiple-choice options. */
function distinctDecimals(correct: number, distractors: number[], digits: number) {
  const used = new Set([correct.toFixed(digits)])
  return distractors.map((value, index) => {
    let candidate = value
    let text = candidate.toFixed(digits)
    while (used.has(text)) {
      candidate += (index + 1) * Math.pow(10, -(digits - 1))
      text = candidate.toFixed(digits)
    }
    used.add(text)
    return text
  })
}

/** Keeps integral option sets valid across every deterministic seed. */
function distinctIntegers(correct: number, distractors: number[]) {
  const used = new Set([correct])
  return distractors.map((value, index) => {
    let candidate = value
    while (used.has(candidate)) candidate += index % 2 === 0 ? index + 2 : -(index + 2)
    used.add(candidate)
    return candidate
  })
}

export function annuityQuestions(seed: AnnuitySeed): Question[] {
  const passageText = `${seed.title}. Số tiền vay ban đầu là ${seed.principal} triệu đồng với lãi suất ${seed.rate}%/tháng. Mỗi tháng, người vay trả đều đặn ${seed.m} triệu đồng vào cuối tháng.`
  
  const base = {
    ...mathMeta,
    knowledge: ['algebra'] as KnowledgeCode[],
    competency: ['quantitative-reasoning', 'modelling'] as CompetencyCode[],
    topic: 'Cấp số nhân - Trả góp',
    passageTitle: 'Toán thực tế: Trả góp ngân hàng',
    passageText,
    tags: ['original', 'authored-variant', 'math', 'annuity', 'hardcore'],
    aiContext: JSON.stringify(seed),
  }
  
  const r = seed.rate / 100;
  
  // M2: Debt after k years
  const kYears = 2; // Fixed at 2 years for simplicity of generation, or random? Let's use 2 years = 24 months.
  const n24 = 24;
  const debt24 = seed.principal * Math.pow(1+r, n24) - seed.m * (Math.pow(1+r, n24) - 1) / r;
  
  // M3: Number of months to pay off
  const targetMonths = Math.log(seed.m / (seed.m - seed.principal * r)) / Math.log(1+r);
  
  return [
    {
      ...base, id: `VACT-MATH-AUTH-ANN-${seed.id}-01`, difficulty: 3, cognitiveLevel: 'M2', type: 'single-choice',
      stem: `Công thức nào sau đây tính đúng số nợ còn lại $D_n$ sau $n$ tháng? (Gọi $P$ là số tiền vay, $r$ là lãi suất tháng, $M$ là số tiền trả hàng tháng).`,
      choices: [
        option('A', `$D_n = P(1+r)^n - M \\frac{(1+r)^n - 1}{r}$`), 
        option('B', `$D_n = P(1+r)^n - M \\cdot n$`), 
        option('C', `$D_n = (P - M)(1+r)^n$`), 
        option('D', `$D_n = P(1+r)^n - M \\frac{(1+r)^n + 1}{r}$`)
      ], 
      answer: 'A',
      solution: `Sau tháng thứ nhất: $D_1 = P(1+r) - M$. Sau tháng thứ hai: $D_2 = D_1(1+r) - M = P(1+r)^2 - M(1+r) - M$. Quy nạp lên n tháng, phần bị trừ đi tạo thành một cấp số nhân: $M + M(1+r) + ... + M(1+r)^{n-1} = M \\frac{(1+r)^n - 1}{r}$. Vậy $D_n = P(1+r)^n - M \\frac{(1+r)^n - 1}{r}$.`,
      hint1: 'Sử dụng quy luật quy nạp: nợ tháng sau bằng (nợ tháng trước × (1+r)) - tiền trả.',
      hint2: `Tổng số tiền đã trả và sinh lãi tạo thành cấp số nhân.`,
      estimatedSeconds: 60, variantGroup: 'math-authored-ann-v1',
    },
    {
      ...base, id: `VACT-MATH-AUTH-ANN-${seed.id}-02`, difficulty: 3, cognitiveLevel: 'M2', type: 'single-choice',
      stem: `Sau đúng 2 năm (24 tháng), số tiền nợ còn lại là khoảng bao nhiêu triệu đồng?`,
      choices: [
        option('A', `${(debt24 + 15).toFixed(1)}`), 
        option('B', `${(seed.principal * Math.pow(1+r, 24) - seed.m * 24).toFixed(1)}`), // Trap: simple minus
        option('C', `${(debt24 - 12).toFixed(1)}`), 
        option('D', `${debt24.toFixed(1)}`)
      ], 
      answer: 'D',
      solution: `Áp dụng công thức: $D_{24} = ${seed.principal}(1+${r})^{24} - ${seed.m} \\frac{(1+${r})^{24} - 1}{${r}} \\approx ${debt24.toFixed(1)}$ triệu đồng.`,
      hint1: 'Áp dụng công thức D_n vừa thiết lập cho n = 24.',
      hint2: `Thay P = ${seed.principal}, r = ${r}, M = ${seed.m}.`,
      estimatedSeconds: 90, variantGroup: 'math-authored-ann-v1',
    },
    {
      ...base, id: `VACT-MATH-AUTH-ANN-${seed.id}-03`, difficulty: 4, cognitiveLevel: 'M3', type: 'single-choice',
      stem: `Cần ít nhất bao nhiêu tháng để người đó trả hết hoàn toàn số nợ?`,
      choices: [
        option('A', `${Math.ceil(targetMonths)}`), 
        option('B', `${Math.ceil(targetMonths) - 1}`), 
        option('C', `${Math.ceil(targetMonths) + 2}`), 
        option('D', `${Math.ceil(targetMonths) + 5}`)
      ], 
      answer: 'A',
      solution: `Để trả hết nợ, $D_n = 0 \\Rightarrow ${seed.principal}(1+${r})^n = ${seed.m} \\frac{(1+${r})^n - 1}{${r}} \\Rightarrow (1+${r})^n = \\frac{${seed.m}}{${seed.m} - ${seed.principal * r}}. Lấy logarit hai vế ta được n ≈ ${targetMonths.toFixed(2)}. Vậy cần ít nhất ${Math.ceil(targetMonths)} tháng.`,
      hint1: 'Cho biểu thức D_n = 0 và giải phương trình tìm n (sử dụng logarit).',
      hint2: `Lấy log cơ số (1+r) của M / (M - P*r).`,
      estimatedSeconds: 120, variantGroup: 'math-authored-ann-v1',
    },
  ]
}

export function bayesQuestions(seed: BayesSeed): Question[] {
  const passageText = `${seed.title} trên diện rộng. Theo thống kê, tỷ lệ mắc bệnh thực sự trong cộng đồng là ${seed.p1}%. Độ nhạy của máy xét nghiệm (xác suất xét nghiệm dương tính nếu có bệnh) là ${seed.p2}%. Tuy nhiên, máy có tỷ lệ dương tính giả (xác suất xét nghiệm dương tính dù không có bệnh) là ${seed.p3}%.`
  
  const base = {
    ...mathMeta,
    knowledge: ['probability-statistics'] as KnowledgeCode[],
    competency: ['logical-reasoning', 'modelling'] as CompetencyCode[],
    topic: 'Xác suất có điều kiện (Bayes)',
    passageTitle: 'Xác suất y tế thực nghiệm',
    passageText,
    tags: ['original', 'authored-variant', 'math', 'bayes', 'hardcore'],
    aiContext: JSON.stringify(seed),
  }
  
  const p_B = seed.p1 / 100;
  const p_notB = 1 - p_B;
  const p_Pos_given_B = seed.p2 / 100;
  const p_Pos_given_notB = seed.p3 / 100;
  
  const p_Pos = p_B * p_Pos_given_B + p_notB * p_Pos_given_notB;
  const p_B_given_Pos = (p_B * p_Pos_given_B) / p_Pos;
  
  const p_Neg_given_notB = 1 - p_Pos_given_notB;
  const p_Neg_given_B = 1 - p_Pos_given_B;
  const p_Neg = p_B * p_Neg_given_B + p_notB * p_Neg_given_notB;
  const p_notB_given_Neg = (p_notB * p_Neg_given_notB) / p_Neg;
  const positiveDistractors = distinctDecimals(p_Pos, [p_Pos + 0.015, p_Pos_given_B * p_B, p_Pos_given_B * p_Pos_given_notB], 4)
  const bayesDistractors = distinctDecimals(p_B_given_Pos, [p_Pos_given_B, 1 - p_Pos_given_notB, p_B_given_Pos - 0.12], 4)
  const negativeDistractors = distinctDecimals(p_notB_given_Neg, [p_Neg_given_notB, 1 - p_B, p_notB_given_Neg - 0.005], 5)
  
  return [
    {
      ...base, id: `VACT-MATH-AUTH-BAYES-${seed.id}-01`, difficulty: 3, cognitiveLevel: 'M2', type: 'single-choice',
      stem: `Chọn ngẫu nhiên một người đi xét nghiệm. Xác suất để người này có kết quả "Dương tính" là bao nhiêu?`,
      choices: [
        option('A', `${p_Pos.toFixed(4)}`),
        option('B', positiveDistractors[0]),
        option('C', positiveDistractors[1]),
        option('D', positiveDistractors[2]),
      ], 
      answer: 'A',
      solution: `Gọi A là biến cố "Kết quả dương tính", B là biến cố "Mắc bệnh". Ta có P(B) = ${p_B}, P(A|B) = ${p_Pos_given_B}, P(A|~B) = ${p_Pos_given_notB}. Xác suất toàn phần P(A) = P(B)P(A|B) + P(~B)P(A|~B) = ${p_B} × ${p_Pos_given_B} + ${p_notB} × ${p_Pos_given_notB} = ${p_Pos.toFixed(4)}.`,
      hint1: 'Sử dụng công thức xác suất toàn phần: P(Dương tính) = P(Bệnh)×P(+|Bệnh) + P(Không Bệnh)×P(+|Không Bệnh).',
      hint2: `Tính ${p_B} × ${p_Pos_given_B} + ${p_notB} × ${p_Pos_given_notB}.`,
      estimatedSeconds: 90, variantGroup: 'math-authored-bayes-v1',
    },
    {
      ...base, id: `VACT-MATH-AUTH-BAYES-${seed.id}-02`, difficulty: 4, cognitiveLevel: 'M3', type: 'single-choice',
      stem: `Một người nhận được kết quả "Dương tính". Hỏi xác suất người này **thực sự mắc bệnh** là bao nhiêu?`,
      choices: [
        option('A', `${p_B_given_Pos.toFixed(4)}`),
        option('B', bayesDistractors[0]),
        option('C', bayesDistractors[1]),
        option('D', bayesDistractors[2]),
      ], 
      answer: 'A',
      solution: `Sử dụng định lý Bayes: P(B|A) = [P(B)P(A|B)] / P(A) = (${p_B} × ${p_Pos_given_B}) / ${p_Pos.toFixed(4)} ≈ ${p_B_given_Pos.toFixed(4)}. Có thể thấy tỷ lệ mắc bệnh thực sự khá thấp so với độ nhạy của máy.`,
      hint1: 'Áp dụng định lý Bayes: Xác suất mắc bệnh khi dương tính = (Xác suất dương tính thật) / (Tổng xác suất dương tính).',
      hint2: `Lấy ${p_B * p_Pos_given_B} chia cho kết quả của câu trước.`,
      estimatedSeconds: 90, variantGroup: 'math-authored-bayes-v1',
    },
    {
      ...base, id: `VACT-MATH-AUTH-BAYES-${seed.id}-03`, difficulty: 4, cognitiveLevel: 'M3', type: 'single-choice',
      stem: `Một người nhận được kết quả "Âm tính". Xác suất người này **không mắc bệnh** (True Negative Predictive Value) là bao nhiêu?`,
      choices: [
        option('A', `${p_notB_given_Neg.toFixed(5)}`),
        option('B', negativeDistractors[0]),
        option('C', negativeDistractors[1]),
        option('D', negativeDistractors[2]),
      ], 
      answer: 'A',
      solution: `Tương tự, P(Không bệnh | Âm tính) = [P(Không bệnh)P(Âm tính | Không bệnh)] / P(Âm tính) = (${p_notB} × ${p_Neg_given_notB}) / ${p_Neg.toFixed(4)} ≈ ${p_notB_given_Neg.toFixed(5)}.`,
      hint1: 'Tìm tổng xác suất Âm tính, sau đó tính tỷ lệ Âm tính thật trên tổng số đó.',
      hint2: `P(Âm tính) = ${p_Neg.toFixed(4)}.`,
      estimatedSeconds: 90, variantGroup: 'math-authored-bayes-v1',
    },
  ]
}

export function optimizationQuestions(seed: OptimizationSeed): Question[] {
  const passageText = `${seed.title}. Một người đứng ở vị trí A trên biển cách bờ (đường thẳng d) một khoảng $h = ${seed.h}$ km. Người này cần đến vị trí B nằm trên bờ biển, cách hình chiếu H của A lên bờ một khoảng $L = ${seed.L}$ km. Người đó chèo thuyền từ A đến một điểm M trên bờ với vận tốc $v_1 = ${seed.v1}$ km/h, sau đó chạy bộ từ M đến B với vận tốc $v_2 = ${seed.v2}$ km/h. Gọi x (km) là khoảng cách từ H đến M ($0 \\le x \\le L$).`
  
  const base = {
    ...mathMeta,
    knowledge: ['algebra'] as KnowledgeCode[],
    competency: ['problem-solving', 'modelling'] as CompetencyCode[],
    topic: 'Tối ưu hóa (Đạo hàm)',
    passageTitle: 'Toán thực tế: Tối ưu thời gian di chuyển',
    passageText,
    tags: ['original', 'authored-variant', 'math', 'optimization', 'hardcore'],
    aiContext: JSON.stringify(seed),
  }
  const minTimeDistractors = distinctDecimals(seed.minTime, [
    seed.minTime + 1.5,
    Math.sqrt(seed.h * seed.h + seed.L * seed.L) / seed.v1,
    seed.minTime - 0.5,
  ], 2)
  
  return [
    {
      ...base, id: `VACT-MATH-AUTH-OPT-${seed.id}-01`, difficulty: 3, cognitiveLevel: 'M2', type: 'single-choice',
      stem: `Hàm số $T(x)$ biểu diễn tổng thời gian di chuyển từ A đến B là gì?`,
      choices: [
        option('A', `$T(x) = \\frac{\\sqrt{${seed.h}^2 + x^2}}{${seed.v1}} + \\frac{${seed.L} - x}{${seed.v2}}$`), 
        option('B', `$T(x) = \\frac{\\sqrt{${seed.h}^2 + x^2}}{${seed.v2}} + \\frac{${seed.L} - x}{${seed.v1}}$`), 
        option('C', `$T(x) = \\frac{${seed.h} + x}{${seed.v1}} + \\frac{${seed.L} - x}{${seed.v2}}$`), 
        option('D', `$T(x) = \\frac{\\sqrt{${seed.h}^2 + (${seed.L} - x)^2}}{${seed.v1}} + \\frac{x}{${seed.v2}}$`)
      ], 
      answer: 'A',
      solution: `Quãng đường chèo thuyền AM = $\\sqrt{h^2 + x^2} = \\sqrt{${seed.h}^2 + x^2}$. Thời gian chèo = $AM / v_1$. Quãng đường chạy MB = $L - x = ${seed.L} - x$. Thời gian chạy = $MB / v_2$. Tổng $T(x) = \\frac{\\sqrt{${seed.h}^2 + x^2}}{${seed.v1}} + \\frac{${seed.L} - x}{${seed.v2}}$.`,
      hint1: 'Sử dụng định lý Pytago để tính khoảng cách AM, sau đó áp dụng Thời gian = Quãng đường / Vận tốc.',
      hint2: `AM = căn(h^2 + x^2).`,
      estimatedSeconds: 60, variantGroup: 'math-authored-opt-v1',
    },
    {
      ...base, id: `VACT-MATH-AUTH-OPT-${seed.id}-02`, difficulty: 4, cognitiveLevel: 'M3', type: 'single-choice',
      stem: `Người đó phải chọn điểm M cách H bao nhiêu km để thời gian di chuyển là ngắn nhất?`,
      choices: [
        option('A', `${seed.optX + 4}`), 
        option('B', `${seed.optX}`), 
        option('C', `${seed.optX - 2}`), 
        option('D', `${seed.L}`) // Trap: run all the way
      ], 
      answer: 'B',
      solution: `Đạo hàm $T'(x) = \\frac{x}{${seed.v1}\\sqrt{${seed.h}^2 + x^2}} - \\frac{1}{${seed.v2}}$. Cho $T'(x) = 0 \\Rightarrow ${seed.v2}x = ${seed.v1}\\sqrt{${seed.h}^2 + x^2} \\Rightarrow ${seed.v2*seed.v2}x^2 = ${seed.v1*seed.v1}(${seed.h*seed.h} + x^2) \\Rightarrow x^2 = ${Math.pow(seed.v1*seed.h, 2) / (seed.v2*seed.v2 - seed.v1*seed.v1)} \\Rightarrow x = ${seed.optX}$.`,
      hint1: 'Tính đạo hàm T\'(x) và giải phương trình T\'(x) = 0.',
      hint2: `Đạo hàm của căn(u) là u'/2căn(u).`,
      estimatedSeconds: 120, variantGroup: 'math-authored-opt-v1',
    },
    {
      ...base, id: `VACT-MATH-AUTH-OPT-${seed.id}-03`, difficulty: 4, cognitiveLevel: 'M3', type: 'single-choice',
      stem: `Thời gian ngắn nhất để hoàn thành quãng đường xấp xỉ bao nhiêu giờ (làm tròn đến hai chữ số thập phân)?`,
      choices: [
        option('A', seed.minTime.toFixed(2)),
        option('B', minTimeDistractors[0]),
        option('C', minTimeDistractors[1]), // Trap: straight-line route
        option('D', minTimeDistractors[2]),
      ], 
      answer: 'A',
      solution: `Thay $x = ${seed.optX}$ vào $T(x)$, ta được $T_{min} = ${seed.minTime}$ giờ.`,
      hint1: 'Thay giá trị x tối ưu vừa tìm được vào hàm T(x).',
      hint2: `T(${seed.optX}) = ?`,
      estimatedSeconds: 60, variantGroup: 'math-authored-opt-v1',
    },
  ]
}

const oxyzTuple = ([x, y, z]: [number, number, number]) => `(${x}; ${y}; ${z})`
const oxyzVector = ([x, y, z]: [number, number, number]) => `(${x}; ${y}; ${z})`

function radicalLatex(value: number) {
  let factor = 1
  let squareFree = value
  for (let k = Math.floor(Math.sqrt(value)); k >= 2; k -= 1) {
    if (value % (k * k) === 0) {
      factor = k
      squareFree = value / (k * k)
      break
    }
  }
  if (squareFree === 1) return `${factor}`
  return factor === 1 ? `\\sqrt{${squareFree}}` : `${factor}\\sqrt{${squareFree}}`
}

/** Original, source-inspired Oxyz: linear extrema on a sphere and tangent planes. */
export function oxyzSphereQuestions(seed: OxyzSphereSeed): Question[] {
  const [a, b, c] = seed.center
  const [u, v, w] = seed.normal
  const scale = seed.radius / 3
  const negativeTangent: [number, number, number] = [a - scale * u, b - scale * v, c - scale * w]
  const alteredPoint: [number, number, number] = [seed.tangentPoint[0] + 1, seed.tangentPoint[1], seed.tangentPoint[2]]
  const tangentSum = -2 * seed.normalDotCenter
  const tangentSumDistractors = distinctIntegers(tangentSum, [2 * seed.normalDotCenter, -seed.maximum, -seed.minimum])
  const base = {
    ...mathMeta,
    knowledge: ['oxyz', 'spatial-geometry'] as KnowledgeCode[],
    competency: ['logical-reasoning', 'problem-solving', 'quantitative-reasoning'] as CompetencyCode[],
    topic: 'Oxyz – Mặt cầu: cực trị và tiếp xúc',
    passageTitle: 'Mặt cầu và mặt phẳng đỡ',
    passageText: `Trong hệ tọa độ $Oxyz$, xét mặt cầu $(S): (x-${a})^2+(y-${b})^2+(z-${c})^2=${seed.radius ** 2}$. Đặt $F(M)=${u}x+(${v})y+(${w})z$ với $M(x;y;z)$ thuộc $(S)$.`,
    tags: ['original', 'authored-variant', 'oxyz', 'sphere', 'tangent-plane', 'extremum', 'parameterized'],
    prerequisites: ['oxyz', 'spatial-geometry'] as KnowledgeCode[],
    misconceptionOptions: ['strategy-choice', 'calculation-slip', 'concept-gap'] as MisconceptionCode[],
    aiContext: JSON.stringify(seed),
  }
  return [
    {
      ...base,
      id: `VACT-MATH-AUTH-OXYZ-SPHERE-${seed.id}-01`,
      difficulty: 5,
      cognitiveLevel: 'M3',
      type: 'single-choice',
      stem: `Giá trị lớn nhất của $F(M)$ là`,
      choices: [
        option('A', `${seed.maximum - 3}`),
        option('B', `${seed.minimum}`),
        option('C', `${seed.maximum}`),
        option('D', `${seed.normalDotCenter}`),
      ],
      answer: 'C',
      solution: `Đặt $\\vec{IM}=(X;Y;Z)$ thì $X^2+Y^2+Z^2=${seed.radius ** 2}$. Khi đó $F(M)=${seed.normalDotCenter}+${u}X+(${v})Y+(${w})Z$. Vì $(${u})^2+(${v})^2+(${w})^2=9$, theo Cauchy–Schwarz: $${u}X+(${v})Y+(${w})Z\\le 3\\cdot ${seed.radius}=${3 * seed.radius}$. Dấu bằng xảy ra khi $\\vec{IM}$ cùng hướng với $(${u};${v};${w})$. Vậy $F_{\\max}=${seed.normalDotCenter}+${3 * seed.radius}=${seed.maximum}$.`,
      hint1: 'Tịnh tiến gốc tọa độ về tâm I của mặt cầu.',
      hint2: `Áp dụng bất đẳng thức $\\vec{u}\\cdot\\vec{IM}\\le |\\vec{u}|\\,|\\vec{IM}|$.`,
      estimatedSeconds: 150,
      variantGroup: 'math-authored-oxyz-sphere-v2',
    },
    {
      ...base,
      id: `VACT-MATH-AUTH-OXYZ-SPHERE-${seed.id}-02`,
      difficulty: 5,
      cognitiveLevel: 'M3',
      type: 'single-choice',
      stem: `Mặt phẳng $P: ${u}x+(${v})y+(${w})z=${seed.maximum}$ là tiếp diện của $(S)$. Tọa độ tiếp điểm là`,
      choices: [
        option('A', `$${oxyzTuple(seed.center)}$`),
        option('B', `$${oxyzTuple(negativeTangent)}$`),
        option('C', `$${oxyzTuple(seed.tangentPoint)}$`),
        option('D', `$${oxyzTuple(alteredPoint)}$`),
      ],
      answer: 'C',
      solution: `Pháp tuyến của $P$ là $\\vec{n}=(${u};${v};${w})$ và $|\\vec n|=3$. Tiếp điểm ở phía làm $F$ lớn nhất nên $\\vec{IT}=\\dfrac{${seed.radius}}{3}\\vec n=${scale}(${u};${v};${w})$. Do đó $T=I+\\vec{IT}=${oxyzTuple(seed.tangentPoint)}$. Kiểm tra: $IT=${seed.radius}$ nên $T\\in(S)$, đồng thời $F(T)=${seed.maximum}$ nên $T\\in P$.`,
      hint1: 'Bán kính nối tâm với tiếp điểm vuông góc tiếp diện.',
      hint2: `Chuẩn hóa pháp tuyến rồi nhân với bán kính ${seed.radius}.`,
      estimatedSeconds: 165,
      variantGroup: 'math-authored-oxyz-sphere-v2',
    },
    {
      ...base,
      id: `VACT-MATH-AUTH-OXYZ-SPHERE-${seed.id}-03`,
      difficulty: 5,
      cognitiveLevel: 'M3',
      type: 'single-choice',
      stem: `Có hai giá trị thực $m_1,m_2$ để mặt phẳng $P_m: ${u}x+(${v})y+(${w})z+m=0$ tiếp xúc với $(S)$. Tổng $m_1+m_2$ bằng`,
      choices: [
        option('A', `${tangentSum}`),
        option('B', `${tangentSumDistractors[0]}`),
        option('C', `${tangentSumDistractors[1]}`),
        option('D', `${tangentSumDistractors[2]}`),
      ],
      answer: 'A',
      solution: `Mặt phẳng $P_m$ tiếp xúc khi khoảng cách từ $I(${a};${b};${c})$ đến $P_m$ bằng bán kính: $\\dfrac{|${seed.normalDotCenter}+m|}{3}=${seed.radius}$. Suy ra $|${seed.normalDotCenter}+m|=${3 * seed.radius}$, nên $m_1=-${seed.normalDotCenter}+${3 * seed.radius}$ và $m_2=-${seed.normalDotCenter}-${3 * seed.radius}$. Vì vậy $m_1+m_2=-2\\cdot ${seed.normalDotCenter}=${-2 * seed.normalDotCenter}$.`,
      hint1: 'Dùng công thức khoảng cách từ tâm mặt cầu đến mặt phẳng.',
      hint2: 'Tiếp xúc tương đương khoảng cách này bằng bán kính.',
      estimatedSeconds: 160,
      variantGroup: 'math-authored-oxyz-sphere-v2',
    },
  ]
}

/** Original, source-inspired Oxyz: a plane section is a hidden circle, not a flat formula. */
export function oxyzSectionQuestions(seed: OxyzSectionSeed): Question[] {
  const [a, b, c] = seed.center
  const height = Math.abs(seed.planeZ - c)
  const sectionCenter: [number, number, number] = [a, b, seed.planeZ]
  const externalPoint: [number, number, number] = [a + seed.inPlaneDistance, b, seed.planeZ]
  const maxDistance = seed.inPlaneDistance + seed.sectionRadius
  const minDistance = seed.inPlaneDistance - seed.sectionRadius
  const radiusDistractors = distinctIntegers(seed.sectionRadius, [height, seed.radius, seed.sectionRadius + 2])
  const maximumDistanceDistractors = distinctIntegers(maxDistance, [minDistance, seed.sectionRadius, seed.inPlaneDistance])
  const base = {
    ...mathMeta,
    knowledge: ['oxyz', 'spatial-geometry'] as KnowledgeCode[],
    competency: ['logical-reasoning', 'problem-solving', 'quantitative-reasoning'] as CompetencyCode[],
    topic: 'Oxyz – Thiết diện mặt cầu và tham số',
    passageTitle: 'Thiết diện ẩn trong không gian',
    passageText: `Cho mặt cầu $(S): (x-${a})^2+(y-${b})^2+(z-${c})^2=${seed.radius ** 2}$ và mặt phẳng $(P): z=${seed.planeZ}$. Giao tuyến của $(S)$ và $(P)$ là đường tròn $(C)$.`,
    tags: ['original', 'authored-variant', 'oxyz', 'sphere-section', 'hidden-circle', 'parameter-existence'],
    prerequisites: ['oxyz', 'spatial-geometry'] as KnowledgeCode[],
    misconceptionOptions: ['strategy-choice', 'calculation-slip', 'concept-gap'] as MisconceptionCode[],
    aiContext: JSON.stringify(seed),
  }
  return [
    {
      ...base,
      id: `VACT-MATH-AUTH-OXYZ-SECTION-${seed.id}-01`,
      difficulty: 4,
      cognitiveLevel: 'M3',
      type: 'single-choice',
      stem: `Bán kính của đường tròn thiết diện $(C)$ bằng`,
      choices: [
        option('A', `${radiusDistractors[0]}`),
        option('B', `${seed.sectionRadius}`),
        option('C', `${radiusDistractors[1]}`),
        option('D', `${radiusDistractors[2]}`),
      ],
      answer: 'B',
      solution: `Hình chiếu vuông góc của tâm $I(${a};${b};${c})$ lên $(P)$ là $H${oxyzTuple(sectionCenter)}$, nên $IH=|${seed.planeZ}-(${c})|=${height}$. Với $M\\in(C)$, tam giác $IHM$ vuông tại $H$, do đó $HM^2=IM^2-IH^2=${seed.radius ** 2}-${height ** 2}=${seed.sectionRadius ** 2}$. Vậy bán kính của $(C)$ là $HM=${seed.sectionRadius}$.`,
      hint1: 'Tìm hình chiếu H của tâm I lên mặt phẳng P.',
      hint2: 'Dùng tam giác vuông IHM với M thuộc đường tròn thiết diện.',
      estimatedSeconds: 120,
      variantGroup: 'math-authored-oxyz-section-v2',
    },
    {
      ...base,
      id: `VACT-MATH-AUTH-OXYZ-SECTION-${seed.id}-02`,
      difficulty: 5,
      cognitiveLevel: 'M3',
      type: 'single-choice',
      stem: `Điểm $A${oxyzTuple(externalPoint)}$ thuộc $(P)$. Khi $M$ chạy trên $(C)$, giá trị lớn nhất của $AM$ là`,
      choices: [
        option('A', `${maximumDistanceDistractors[0]}`),
        option('B', `${maximumDistanceDistractors[1]}`),
        option('C', `${maximumDistanceDistractors[2]}`),
        option('D', `${maxDistance}`),
      ],
      answer: 'D',
      solution: `Trong mặt phẳng $(P)$, $(C)$ là đường tròn tâm $H${oxyzTuple(sectionCenter)}$, bán kính $r=${seed.sectionRadius}$. Ta có $AH=${seed.inPlaneDistance}>r$, nên A ở ngoài đường tròn. Theo bất đẳng thức tam giác, $AM\\le AH+HM=${seed.inPlaneDistance}+${seed.sectionRadius}=${maxDistance}$. Dấu bằng khi $A,H,M$ thẳng hàng và H nằm giữa A, M. Vậy $AM_{\\max}=${maxDistance}$.`,
      hint1: 'Sau khi cắt, hãy giải bài toán trong chính mặt phẳng P.',
      hint2: 'Khoảng cách từ một điểm ngoài đường tròn đến điểm chạy trên đường tròn lớn nhất ở điểm đối diện.',
      estimatedSeconds: 155,
      variantGroup: 'math-authored-oxyz-section-v2',
    },
    {
      ...base,
      id: `VACT-MATH-AUTH-OXYZ-SECTION-${seed.id}-03`,
      difficulty: 5,
      cognitiveLevel: 'M3',
      type: 'single-choice',
      stem: `Với $m$ là số nguyên, mặt phẳng $z=m$ cắt $(S)$ theo một đường tròn có bán kính dương. Có bao nhiêu giá trị của $m$?`,
      choices: [
        option('A', `${2 * seed.radius}`),
        option('B', `${2 * seed.radius - 2}`),
        option('C', `${2 * seed.radius - 1}`),
        option('D', `${2 * seed.radius + 1}`),
      ],
      answer: 'C',
      solution: `Mặt phẳng $z=m$ cắt mặt cầu theo một đường tròn bán kính dương khi khoảng cách từ tâm $I$ đến mặt phẳng nhỏ hơn bán kính: $|m-${c}|<${seed.radius}$. Vì $m$ nguyên, ta có $c-${seed.radius}<m<c+${seed.radius}$. Các giá trị nguyên là $c-${seed.radius}+1,\\ldots,c+${seed.radius}-1$, gồm $2\\cdot${seed.radius}-1=${2 * seed.radius - 1}$ giá trị. Hai đầu mút bị loại vì khi đó chỉ còn tiếp điểm.`,
      hint1: 'Phân biệt “đường tròn có bán kính dương” với trường hợp tiếp xúc.',
      hint2: 'Khoảng cách từ I đến z = m là |m − c|.',
      estimatedSeconds: 160,
      variantGroup: 'math-authored-oxyz-section-v2',
    },
  ]
}

/** Original, source-inspired Oxyz: common perpendicular and distance of two skew lines. */
export function oxyzSkewLinesQuestions(seed: OxyzSkewLinesSeed): Question[] {
  const [a, b, c] = seed.pointA
  const [p, q, r] = seed.pointB
  const [u, v, w] = seed.directionU
  const [s, t, k] = seed.directionV
  const crossText = oxyzVector(seed.cross)
  const closestSum = seed.closestPointOnFirst.reduce((sum, value) => sum + value, 0)
  const distanceText = radicalLatex(seed.distanceRadicand)
  const base = {
    ...mathMeta,
    knowledge: ['oxyz', 'spatial-geometry'] as KnowledgeCode[],
    competency: ['logical-reasoning', 'problem-solving', 'quantitative-reasoning'] as CompetencyCode[],
    topic: 'Oxyz – Hai đường thẳng chéo nhau',
    passageTitle: 'Đường vuông góc chung trong không gian',
    passageText: `Xét hai đường thẳng $d_1: (x;y;z)=(${a};${b};${c})+\\lambda(${u};${v};${w})$ và $d_2: (x;y;z)=(${p};${q};${r})+\\mu(${s};${t};${k})$.`,
    tags: ['original', 'authored-variant', 'oxyz', 'skew-lines', 'common-perpendicular', 'triple-product'],
    prerequisites: ['oxyz', 'spatial-geometry'] as KnowledgeCode[],
    misconceptionOptions: ['strategy-choice', 'calculation-slip', 'concept-gap'] as MisconceptionCode[],
    aiContext: JSON.stringify(seed),
  }
  return [
    {
      ...base,
      id: `VACT-MATH-AUTH-OXYZ-SKEW-${seed.id}-01`,
      difficulty: 4,
      cognitiveLevel: 'M2',
      type: 'single-choice',
      stem: `Vị trí tương đối của $d_1$ và $d_2$ là`,
      choices: [option('A', 'Song song'), option('B', 'Trùng nhau'), option('C', 'Cắt nhau'), option('D', 'Chéo nhau')],
      answer: 'D',
      solution: `Hai vectơ chỉ phương $\\vec u=${oxyzVector(seed.directionU)}$ và $\\vec v=${oxyzVector(seed.directionV)}$ không cùng phương. Hơn nữa $\\vec u\\times\\vec v=${crossText}$ và theo cách phân tích $\\overrightarrow{AB}=${seed.alpha}\\vec u+(${seed.beta})\\vec v+${seed.gamma}(\\vec u\\times\\vec v)$. Thành phần theo $\\vec u\\times\\vec v$ khác 0 nên $A,B$ không cùng nằm trong mặt phẳng chứa $d_1$ và song song với $d_2$. Vậy hai đường thẳng không cắt nhau và chéo nhau.`,
      hint1: 'Kiểm tra hai vectơ chỉ phương có cùng phương hay không.',
      hint2: 'Dùng tích hỗn tạp hoặc thành phần theo u × v để xét đồng phẳng.',
      estimatedSeconds: 140,
      variantGroup: 'math-authored-oxyz-skew-v2',
    },
    {
      ...base,
      id: `VACT-MATH-AUTH-OXYZ-SKEW-${seed.id}-02`,
      difficulty: 5,
      cognitiveLevel: 'M3',
      type: 'single-choice',
      stem: `Khoảng cách giữa $d_1$ và $d_2$ bằng`,
      choices: [
        option('A', `$${radicalLatex(seed.distanceRadicand + 1)}$`),
        option('B', `$${distanceText}$`),
        option('C', `$${radicalLatex(Math.max(1, seed.distanceRadicand - 1))}$`),
        option('D', `$${radicalLatex(seed.distanceRadicand + 6)}$`),
      ],
      answer: 'B',
      solution: `Vì $\\vec w=\\vec u\\times\\vec v=${crossText}$ vuông góc với cả hai đường thẳng, khoảng cách là độ dài hình chiếu của $\\overrightarrow{AB}$ lên phương $\\vec w$. Từ $\\overrightarrow{AB}=${seed.alpha}\\vec u+(${seed.beta})\\vec v+${seed.gamma}\\vec w$, phần vuông góc chung là ${seed.gamma}$ lần $\\vec w$. Do đó $d(d_1,d_2)=|${seed.gamma}|\\,|\\vec w|=\\sqrt{${seed.distanceRadicand}}=${distanceText}$.`,
      hint1: 'Lấy tích có hướng của hai vectơ chỉ phương để có hướng vuông góc chung.',
      hint2: 'Khoảng cách là độ dài thành phần của AB theo hướng u × v.',
      estimatedSeconds: 180,
      variantGroup: 'math-authored-oxyz-skew-v2',
    },
    {
      ...base,
      id: `VACT-MATH-AUTH-OXYZ-SKEW-${seed.id}-03`,
      difficulty: 5,
      cognitiveLevel: 'M3',
      type: 'single-choice',
      stem: `Gọi $P$ là đầu mút thuộc $d_1$ của đoạn vuông góc chung giữa $d_1,d_2$. Giá trị $x_P+y_P+z_P$ bằng`,
      choices: [
        option('A', `${closestSum + 2}`),
        option('B', `${closestSum - 1}`),
        option('C', `${closestSum}`),
        option('D', `${closestSum + 4}`),
      ],
      answer: 'C',
      solution: `Ta có $\\overrightarrow{AB}=${seed.alpha}\\vec u+(${seed.beta})\\vec v+${seed.gamma}(\\vec u\\times\\vec v)$. Chọn $P=A+${seed.alpha}\\vec u$ trên $d_1$ và $Q=B-${seed.beta}\\vec v$ trên $d_2$. Khi đó $\\overrightarrow{PQ}=${seed.gamma}(\\vec u\\times\\vec v)$, vuông góc với cả $d_1,d_2$, nên P đúng là đầu mút cần tìm. Suy ra $P${oxyzTuple(seed.closestPointOnFirst)}$, vì thế $x_P+y_P+z_P=${closestSum}$.`,
      hint1: 'Tách AB theo ba hướng u, v và u × v.',
      hint2: 'Khử thành phần theo u bằng cách chọn P trên d1, rồi khử thành phần theo v khi chọn Q trên d2.',
      estimatedSeconds: 190,
      variantGroup: 'math-authored-oxyz-skew-v2',
    },
  ]
}


export function pharmacokineticsQuestions(seed: PharmacokineticsSeed): Question[] {
  const passageText = `Sự hấp thụ thuốc của một bệnh nhân được mô hình hóa bằng hàm số toán học. Nồng độ của một loại thuốc trong máu của bệnh nhân (tính bằng mg/L) sau khi tiêm $t$ giờ được xác định bởi công thức: $C(t) = ${seed.a} \\cdot t \\cdot e^{-${seed.b}t}$ với $t > 0$.`
  
  const base = {
    ...mathMeta,
    knowledge: ['algebra'] as KnowledgeCode[],
    competency: ['modelling', 'problem-solving'] as CompetencyCode[],
    topic: 'Ứng dụng Đạo hàm (Y học)',
    passageTitle: 'Dược động học: Nồng độ thuốc',
    passageText,
    tags: ['original', 'authored-variant', 'math', 'pharmacokinetics', 'hardcore'],
    aiContext: JSON.stringify(seed),
  }
  
  const t_max = 1 / seed.b; // Derivative C'(t) = a*e^(-bt) - a*b*t*e^(-bt) = a*e^(-bt)(1 - bt). Roots at bt = 1 => t = 1/b.
  const c_max = seed.a * t_max * Math.exp(-1); 
  const c_max_e = seed.a * t_max; // The answer in terms of e^-1
  
  // Rate of change at t = 2 * t_max (which is 2/b)
  const t_eval = 2 * t_max;
  const rate_eval = seed.a * Math.exp(-seed.b * t_eval) * (1 - seed.b * t_eval);
  
  const rateDistractors = distinctDecimals(rate_eval, [
    Math.abs(rate_eval),
    seed.a * Math.exp(-seed.b * t_eval),
    seed.a * (1 - seed.b * t_eval)
  ], 4)

  return [
    {
      ...base, id: `VACT-MATH-AUTH-PHARMA-${seed.id}-01`, difficulty: 3, cognitiveLevel: 'M2', type: 'single-choice',
      stem: `Nồng độ thuốc trong máu của bệnh nhân đạt mức cao nhất sau bao nhiêu giờ kể từ khi tiêm?`,
      choices: [
        option('A', `${t_max} giờ`),
        option('B', `${t_max * 2} giờ`),
        option('C', `${t_max + 1.5} giờ`),
        option('D', `${t_max / 2} giờ`)
      ], 
      answer: 'A',
      solution: `Đạo hàm $C'(t) = ${seed.a} \\cdot e^{-${seed.b}t} - ${seed.a} \\cdot ${seed.b}t \\cdot e^{-${seed.b}t} = ${seed.a} e^{-${seed.b}t}(1 - ${seed.b}t)$. Cho $C'(t) = 0 \\Rightarrow 1 - ${seed.b}t = 0 \\Rightarrow t = ${t_max}$. Vì $C'(t)$ đổi dấu từ dương sang âm khi qua $t = ${t_max}$, nồng độ đạt cực đại sau ${t_max} giờ.`,
      hint1: 'Tính đạo hàm C\'(t) và tìm nghiệm của phương trình C\'(t) = 0.',
      hint2: `Sử dụng quy tắc đạo hàm của tích: (uv)' = u'v + uv'. Nhớ đạo hàm của e^(-bt) là -b*e^(-bt).`,
      estimatedSeconds: 90, variantGroup: 'math-authored-pharma-v1',
    },
    {
      ...base, id: `VACT-MATH-AUTH-PHARMA-${seed.id}-02`, difficulty: 4, cognitiveLevel: 'M3', type: 'numeric',
      stem: `Tốc độ thay đổi nồng độ thuốc trong máu tại thời điểm $t = ${t_eval}$ giờ là bao nhiêu mg/L/giờ? (Làm tròn đến 4 chữ số thập phân).`,
      answer: rate_eval.toFixed(4),
      solution: `Tốc độ thay đổi chính là đạo hàm tại thời điểm đó: $C'(${t_eval}) = ${seed.a} e^{-${seed.b} \\cdot ${t_eval}}(1 - ${seed.b} \\cdot ${t_eval}) = ${seed.a} e^{-2}(1 - 2) = -${seed.a} e^{-2} \\approx ${rate_eval.toFixed(4)}$ (mg/L/giờ).`,
      hint1: 'Tốc độ thay đổi là giá trị của đạo hàm C\'(t) tại t đã cho.',
      hint2: `Thay t = ${t_eval} vào C'(t) đã tính ở câu trước.`,
      estimatedSeconds: 90, variantGroup: 'math-authored-pharma-v1',
    },
    {
      ...base, id: `VACT-MATH-AUTH-PHARMA-${seed.id}-03`, difficulty: 4, cognitiveLevel: 'M2', type: 'single-choice',
      stem: `Nồng độ thuốc cao nhất trong máu của bệnh nhân là bao nhiêu?`,
      choices: [
        option('A', `$\\frac{${c_max_e}}{e}$ mg/L`),
        option('B', `$\\frac{${c_max_e * 2}}{e}$ mg/L`),
        option('C', `$\\frac{${seed.a}}{e^2}$ mg/L`),
        option('D', `$${c_max_e} e$ mg/L`)
      ], 
      answer: 'A',
      solution: `Thay $t = ${t_max}$ vào hàm số: $C(${t_max}) = ${seed.a} \\cdot ${t_max} \\cdot e^{-${seed.b} \\cdot ${t_max}} = ${c_max_e} \\cdot e^{-1} = \\frac{${c_max_e}}{e}$ mg/L.`,
      hint1: 'Thay giá trị thời gian t làm nồng độ đạt cực đại vào hàm số C(t).',
      hint2: `C(${t_max}) = ?`,
      estimatedSeconds: 60, variantGroup: 'math-authored-pharma-v1',
    },
  ]
}

const generatedAnnuitySeeds = Array.from({ length: 10 }, (_, i) => generateAnnuitySeed(`gen-ann-${i + 1}`))
const generatedBayesSeeds = Array.from({ length: 10 }, (_, i) => generateBayesSeed(`gen-bayes-${i + 1}`))
const generatedOptimizationSeeds = Array.from({ length: 10 }, (_, i) => generateOptimizationSeed(`gen-opt-${i + 1}`))
const generatedOxyzSphereSeeds = Array.from({ length: 10 }, (_, i) => generateOxyzSphereSeed(`gen-oxyz-sphere-${i + 1}`))
const generatedOxyzSectionSeeds = Array.from({ length: 10 }, (_, i) => generateOxyzSectionSeed(`gen-oxyz-section-${i + 1}`))
const generatedOxyzSkewLinesSeeds = Array.from({ length: 10 }, (_, i) => generateOxyzSkewLinesSeed(`gen-oxyz-skew-${i + 1}`))
const generatedPharmacokineticsSeeds = Array.from({ length: 10 }, (_, i) => generatePharmacokineticsSeed(`gen-pharma-${i + 1}`))
// Chapter 1 is intentionally sampled more densely: its 16 reviewed families
// cover the full function-analysis chapter without copying source prompts.
const generatedChapter1Seeds = Array.from({ length: 24 }, (_, i) => generateChapter1Seed(`gen-ch1-functions-${i + 1}`))
const generatedK11TrigonometrySeeds = Array.from({ length: 20 }, (_, i) => generateTrigonometrySeed(`gen-k11-trig-${i + 1}`))
const generatedK11ExpLogSeeds = Array.from({ length: 20 }, (_, i) => generateExpLogSeed(`gen-k11-exp-log-${i + 1}`))
const generatedK11SequenceSeeds = Array.from({ length: 20 }, (_, i) => generateSequenceSeed(`gen-k11-sequences-${i + 1}`))
const generatedK11ParallelSeeds = Array.from({ length: 20 }, (_, i) => generateParallelSeed(`gen-k11-parallel-${i + 1}`))
const generatedK11LimitSeeds = Array.from({ length: 20 }, (_, i) => generateLimitSeed(`gen-k11-limits-${i + 1}`))
const generatedK11StatisticsSeeds = Array.from({ length: 20 }, (_, i) => generateStatisticsSeed(`gen-k11-statistics-${i + 1}`))
const generatedK11DerivativeSeeds = Array.from({ length: 20 }, (_, i) => generateDerivativeSeed(`gen-k11-derivatives-${i + 1}`))

/** Parameterized DGNl math families. */
export const authoredMathVariants: Question[] = [
  ...generatedAnnuitySeeds.flatMap(annuityQuestions),
  ...generatedBayesSeeds.flatMap(bayesQuestions),
  ...generatedOptimizationSeeds.flatMap(optimizationQuestions),
  ...generatedOxyzSphereSeeds.flatMap(oxyzSphereQuestions),
  ...generatedOxyzSectionSeeds.flatMap(oxyzSectionQuestions),
  ...generatedOxyzSkewLinesSeeds.flatMap(oxyzSkewLinesQuestions),
  ...generatedPharmacokineticsSeeds.flatMap(pharmacokineticsQuestions),
  ...generatedChapter1Seeds.flatMap(chapter1FunctionQuestions),
  ...generatedK11TrigonometrySeeds.flatMap(k11TrigonometryQuestions),
  ...generatedK11ExpLogSeeds.flatMap(k11ExpLogQuestions),
  ...generatedK11SequenceSeeds.flatMap(k11SequenceQuestions),
  ...generatedK11ParallelSeeds.flatMap(k11ParallelGeometryQuestions),
  ...generatedK11LimitSeeds.flatMap(k11LimitContinuityQuestions),
  ...generatedK11StatisticsSeeds.flatMap(k11GroupedStatisticsQuestions),
  ...generatedK11DerivativeSeeds.flatMap(k11DerivativeQuestions),
]

/** Build a fresh, deterministic set of original parameterized math items. */
export function createMathVariantPack(seed: string): Question[] {
  return [
    ...annuityQuestions(generateAnnuitySeed(`${seed}-annuity`)),
    ...bayesQuestions(generateBayesSeed(`${seed}-bayes`)),
    ...optimizationQuestions(generateOptimizationSeed(`${seed}-optimization`)),
    ...oxyzSphereQuestions(generateOxyzSphereSeed(`${seed}-oxyz-sphere`)),
    ...oxyzSectionQuestions(generateOxyzSectionSeed(`${seed}-oxyz-section`)),
    ...oxyzSkewLinesQuestions(generateOxyzSkewLinesSeed(`${seed}-oxyz-skew`)),
    ...pharmacokineticsQuestions(generatePharmacokineticsSeed(`${seed}-pharma`)),
    ...chapter1FunctionQuestions(generateChapter1Seed(`${seed}-chapter-1-functions`)),
    ...k11TrigonometryQuestions(generateTrigonometrySeed(`${seed}-k11-trigonometry`)),
    ...k11ExpLogQuestions(generateExpLogSeed(`${seed}-k11-exp-log`)),
    ...k11SequenceQuestions(generateSequenceSeed(`${seed}-k11-sequences`)),
    ...k11ParallelGeometryQuestions(generateParallelSeed(`${seed}-k11-parallel`)),
    ...k11LimitContinuityQuestions(generateLimitSeed(`${seed}-k11-limits`)),
    ...k11GroupedStatisticsQuestions(generateStatisticsSeed(`${seed}-k11-statistics`)),
    ...k11DerivativeQuestions(generateDerivativeSeed(`${seed}-k11-derivatives`)),
  ]
}
